/**
 * Technology keywords for syntax highlighting
 *
 * These keywords are highlighted in formatted text to emphasize
 * technology terms in portfolio content, project descriptions, etc.
 */

/**
 * List of technology keywords to highlight
 *
 * Organized by category for maintainability
 */
export const TECH_KEYWORDS: string[] = [
  // Frontend Frameworks & Libraries
  'React',
  'Vue',
  'Angular',
  'Svelte',
  'Next.js',

  // Programming Languages
  'TypeScript',
  'JavaScript',
  'Python',
  'Go',
  'Rust',
  'Java',
  'C++',
  'C#',
  'PHP',
  'Ruby',
  'Swift',
  'Kotlin',

  // Backend & Runtime
  'Node.js',
  'Deno',
  'Express',
  'FastAPI',
  'Django',
  'Flask',
  'Spring',

  // Databases
  'MongoDB',
  'PostgreSQL',
  'MySQL',
  'Redis',
  'Elasticsearch',
  'DynamoDB',
  'Firestore',
  'SQLite',

  // Cloud & Infrastructure
  'Docker',
  'Kubernetes',
  'AWS',
  'Azure',
  'GCP',
  'Heroku',
  'Vercel',
  'Netlify',
  'DigitalOcean',

  // APIs & Protocols
  'GraphQL',
  'REST',
  'API',
  'gRPC',
  'WebSocket',
  'HTTP',
  'HTTPS',

  // AI & Machine Learning
  'AI',
  'ML',
  'TensorFlow',
  'PyTorch',
  'Keras',
  'OpenAI',
  'LLM',
  'GPT',
  'NLP',

  // Testing & Quality
  'Jest',
  'Mocha',
  'Pytest',
  'Cypress',
  'Playwright',
  'Selenium',

  // Version Control & CI/CD
  'Git',
  'GitHub',
  'GitLab',
  'Bitbucket',
  'CI/CD',
  'Jenkins',
  'CircleCI',
  'Travis CI',

  // Development Methodologies
  'DevOps',
  'Agile',
  'Scrum',
  'Kanban',
  'TDD',
  'BDD',

  // Build Tools & Bundlers
  'Webpack',
  'Vite',
  'Rollup',
  'Parcel',
  'esbuild',
  'Babel',
  'ESLint',
  'Prettier',

  // Mobile Development
  'React Native',
  'Flutter',
  'Expo',
  'iOS',
  'Android',

  // Other Technologies
  'WebAssembly',
  'PWA',
  'SPA',
  'SSR',
  'SSG',
  'JAMstack',
  'Serverless',
  'Microservices',
  'OAuth',
  'JWT',
  'WebGL',
  'Three.js',
]

/**
 * Check if a word is a technology keyword
 *
 * @param word - Word to check
 * @returns true if the word is a recognized technology keyword
 *
 * @example
 * ```typescript
 * isTechKeyword('React')      // true
 * isTechKeyword('TypeScript') // true
 * isTechKeyword('random')     // false
 * ```
 */
export function isTechKeyword(word: string): boolean {
  return TECH_KEYWORDS.includes(word)
}

/**
 * Get all tech keywords matching a pattern
 *
 * @param pattern - Regular expression pattern
 * @returns Array of matching keywords
 *
 * @example
 * ```typescript
 * getKeywordsByPattern(/^React/) // ['React', 'React Native']
 * getKeywordsByPattern(/Script$/) // ['TypeScript', 'JavaScript']
 * ```
 */
export function getKeywordsByPattern(pattern: RegExp): string[] {
  return TECH_KEYWORDS.filter(keyword => pattern.test(keyword))
}
